import React, { useCallback, useEffect, useState } from "react";

import { Alert, Box, Stack, Typography } from "@mui/material";

import { ApplicationEndpoints } from "@generated/application";
import { useApi } from "src/platform/hooks/useApi";

import { PrimaryLanguage, StackTemplate, StackType } from "../../types";

import { LANGUAGE_LABEL } from "./template-gallery-constants";
import { TemplateListItem, TemplateSkeleton } from "./TemplateCard";
import { TemplateLanguageFilter } from "./TemplateFilters";

interface StepChooseTemplateProps {
  stackType: StackType;
  value: StackTemplate | null;
  onChange: (template: StackTemplate | null) => void;
  /** Auto-select the template matching this slug after loading (restored from URL state) */
  preselectedSlug?: string;
  cloudProvider?: string;
}

export default function StepChooseTemplate({
  stackType,
  value,
  onChange,
  preselectedSlug,
  cloudProvider,
}: StepChooseTemplateProps) {
  const { get } = useApi();
  const [templates, setTemplates] = useState<StackTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Auto-select preselected template after templates load (restores URL state on refresh)
  useEffect(() => {
    if (!value && preselectedSlug && templates.length > 0) {
      const match = templates.find((t) => t.slug === preselectedSlug);
      if (match) onChange(match);
    }
  }, [templates, preselectedSlug, value, onChange]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await get<StackTemplate[]>(
        `${ApplicationEndpoints.LIST_STACK_TEMPLATES}?systemType=${encodeURIComponent(stackType)}`,
        {
          errorMessage: "Failed to load stack templates",
          showErrorNotification: false,
        },
      );
      if (result === null) {
        setError(
          "Could not reach the template service. Make sure the API server is running.",
        );
        return;
      }
      const all = Array.isArray(result) ? result : [];
      // K8s-only model: all templates target Kubernetes. No deployment-mode
      // filtering is needed.
      setTemplates(all);
    } catch {
      setError("Failed to load templates. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [get, stackType]);

  useEffect(() => {
    void load();
  }, [load]);

  // Filter state
  const [languageFilter, setLanguageFilter] = useState<PrimaryLanguage | null>(
    null,
  );

  // Selected template triggers expansion
  const [expandedSlug, setExpandedSlug] = useState<string | null>(null);

  // Auto-expand selected template
  useEffect(() => {
    if (value) {
      setExpandedSlug(value.slug);
    }
  }, [value]);

  // Filter templates by language
  const filteredTemplates = languageFilter
    ? templates.filter((t) => t.language === languageFilter)
    : templates;

  if (loading) return <TemplateSkeleton />;

  if (error) {
    return (
      <Alert severity="error" onClose={() => setError(null)}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Choose the technology stack for this stack.
      </Typography>

      {/* Language filter chips */}
      <TemplateLanguageFilter
        languageFilter={languageFilter}
        onChange={setLanguageFilter}
      />

      {/* List view with inline diagrams - selected item shows diagram */}
      <Stack spacing={1}>
        {filteredTemplates.map((tmpl) => (
          <TemplateListItem
            key={tmpl.slug}
            template={tmpl}
            cloudProvider={cloudProvider ?? ""}
            selected={value?.slug === tmpl.slug}
            expanded={expandedSlug === tmpl.slug}
            onSelect={(template) => {
              onChange(template);
              setExpandedSlug(template.slug);
            }}
            onExpand={(template) => {
              setExpandedSlug(
                expandedSlug === template.slug ? null : template.slug,
              );
            }}
          />
        ))}
      </Stack>

      {filteredTemplates.length === 0 && (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <Typography variant="body2" color="text.secondary">
            No templates found
            {languageFilter ? ` for ${LANGUAGE_LABEL[languageFilter]}` : ""}.
            Try a different option.
          </Typography>
        </Box>
      )}
    </Box>
  );
}
